import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';

interface RequireRoleProps {
  /** Roles allowed to view the wrapped page; any match grants access. */
  roles: string[];
  children: ReactNode;
  /** Human page name for the forbidden message (e.g. "Notifications"). */
  label?: string;
}

/**
 * Page-level access guard. Wraps a settings page body so a direct-URL visit by a
 * user who lacks the required role gets an on-route forbidden state rather than
 * the full page (which is otherwise only protected at the API layer).
 *
 * Waits for auth to initialize before deciding (no flash-of-forbidden during the
 * token-refresh window), matching the gate-on-init behaviour of the existing
 * useRequireRole hook — but renders inline-forbidden on-route instead of
 * redirecting, matching the SsoConfigPage shape.
 */
export function RequireRole({ roles, children, label }: RequireRoleProps) {
  const { isInitialized, hasAnyRole } = useAuthStore();

  // Still bootstrapping auth (token refresh on load) — defer the decision.
  if (!isInitialized) return null;

  if (!hasAnyRole(roles)) {
    return (
      <div className="flex flex-col gap-2 py-12 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-text-muted" />
        <p className="text-sm text-text-secondary">
          You don't have permission to view {label ?? 'this page'}.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
